import type { AppState } from '../state/store';
import { versionedAssetUrl } from '../assets/version';
import { btn, esc } from './components';

interface DocsSection {
  id: string;
  title: string;
  eyebrow?: string;
  body: string[];
  bullets?: string[];
  table?: { columns: string[]; rows: string[][] };
}

const DOC_SECTIONS: DocsSection[] = [
  {
    id: 'overview',
    title: 'Overview',
    eyebrow: 'Game first',
    body: [
      'Gravelight is a dark-fantasy, pixel-art, turn-based tactical extraction roguelite. You guide one runner through a one-way Descent, gather Gold and relics, then decide when to extract before the dark overtakes the lantern.',
      'The loop is Waystation to Reward or Echo Descent, loot, extract or die, bank and seal what survives, then trade or spend back at the Waystation.',
    ],
    bullets: [
      'Greed is light, and the dark is always counting.',
      'The browser client renders the run; the backend is authoritative for actions and economy state.',
      'The economy supports the game loop. It is not the reason the game should be fun.',
    ],
  },
  {
    id: 'getting-started',
    title: 'Getting Started',
    eyebrow: 'Wallet and access',
    body: [
      'Connect a Solana wallet and sign an off-chain login message. Signing in does not spend $OBOL and does not send a transaction.',
      'Reward runs require token eligibility at run-start. If you are not eligible or have no Ferry Pass, you can still use Echo runs for practice and Spectate for a read-only preview.',
    ],
    bullets: [
      'Use Re-check to ask the backend for a fresh token eligibility read.',
      'A confirmed under-threshold check can freeze Reward-run access until a successful re-check.',
      'An RPC failure is treated as unverified: no Pass is spent and the failed read does not freeze the account.',
    ],
  },
  {
    id: 'waystation',
    title: 'Waystation',
    eyebrow: 'The hub',
    body: [
      'The Waystation is the social hub between runs. Walk the plaza with WASD or the arrow keys and press E at a station to open it: the Dungeon Gate, Inventory, Merchant, Marketplace, Gold Exchange, the Ferryman’s Wager spinner, and Cosmetics.',
      'Other players appear in the plaza in real time. Presence is visual and social only — nobody can touch your account, items, or Gold from the hub.',
    ],
    bullets: [
      'Emotes are the only hub communication; there is no chat.',
      'Every station can also be opened from the top navigation tabs.',
      'Equipped cosmetic skins are visible to everyone in the plaza.',
    ],
  },
  {
    id: 'runs',
    title: 'Runs',
    eyebrow: 'Reward vs Echo',
    body: [
      'Reward runs are the bankable mode. They require token eligibility and spend one Ferry Pass at start. Successful extraction can bank Gold and seal eligible items.',
      'Extraction begins at floor 3. Loot found before floor 3 can still be sealed if you survive to a floor 3+ ferry-step.',
      'Echo runs are always-available practice. Practice only. Loot and Gold disappear when the Echo ends.',
    ],
    table: {
      columns: ['Run type', 'Requires', 'Result'],
      rows: [
        ['Reward', '$OBOL eligibility and 1 Ferry Pass', 'Bankable Gold and sealed items after successful extraction'],
        ['Echo', 'No token gate and no Pass', 'Practice only; no rewards or economy progression'],
      ],
    },
    bullets: [
      'Floor Gold at launch tuning: floors 1–2 bank nothing; floors 3–10 bank a flat 200 Gold each.',
      'Lantern fuel drains as turns pass.',
      'Every run includes a free base kit: Oil 2 / Torch 2 / Potion 2.',
      'Oil, Torch, and Potion are finite run charges. Oil restores base +5 Lantern, Torch restores base +3 Lantern, and Potion heals base +4 HP before any survival gear bonus; capped uses report configured, modified, applied, and capped amounts.',
      'Burden rises with carried Gold and items, making the dark more dangerous.',
      'Extraction banks eligible haul; death or abandon loses carried haul.',
    ],
  },
  {
    id: 'merchant',
    title: 'Merchant',
    eyebrow: 'Supplies, repair, salvage',
    body: [
      'The Merchant sells account-bound supplies for Gold, repairs damaged persistent gear, and salvages eligible junk gear for a low fixed Gold return. These mutations are tracked by the backend ledger.',
      'Reward runs include the free base kit, then load selected extras from account stock up to the current extra cap. Echo runs use practice supplies and do not consume account stock.',
    ],
    table: {
      columns: ['Supply', 'Run effect', 'Notes'],
      rows: [
        ['Oil Flask', '+5 Lantern fuel', 'Finite charge; cannot be used at full fuel'],
        ['Torch Bundle', '+3 Lantern fuel', 'Finite charge; simple fuel refill'],
        ['Healing Draught', '+4 HP', 'Finite charge; cannot exceed max HP or be used at full HP'],
      ],
    },
    bullets: [
      'Base kit is free and does not spend account stock.',
      'Reward extra supplies auto-fill by default and can be reduced before starting to save stock.',
      'Selected Reward extras are spent from account stock at run start.',
      'Echo supplies are practice-only and never grant progression.',
      'Using a supply still consumes a turn; Potion healing and enemy damage appear as separate log events.',
      'Repairs restore damaged owned gear to max durability for a rarity-scaled Gold cost.',
      'Salvage values are fixed by rarity only: no affix premium, stat premium, marketplace pricing, or $OBOL conversion.',
      'Normal sealed dungeon drops can be salvaged if they are owned, sealed, unequipped, unlisted, and banked. Account-bound gear can be salvaged here.',
      'Equipped, listed, unowned, unsealed/carried, cosmetic, prestige, and unsupported-source items cannot be salvaged.',
      'Listed marketplace items are not repairable while locked.',
      'The Merchant does not sell weapons, armor, lanterns, trinkets, Ferry Passes, keys, crafting materials, or power gear.',
    ],
  },
  {
    id: 'ferry-passes',
    title: 'Ferry Passes',
    eyebrow: 'Emission throttle',
    body: [
      'Ferry Passes are account-bound and are spent only to start Reward runs. They are not purchasable in v1.',
      'Passes regenerate over time up to a cap after the account has activated through a successful eligibility check.',
    ],
    bullets: [
      'Default cap: 6 Passes.',
      'Default regen: +1 Ferry Pass every 4 hours while active.',
      'Full refill from 0 to 6 takes 24 hours.',
      'Unactivated accounts do not bank Pass regen yet.',
      'Frozen accounts keep existing Passes but cannot spend or regenerate until eligibility is restored.',
      'Unverified RPC reads deny the attempted Reward start gracefully without spending a Pass.',
    ],
  },
  {
    id: 'items-sealing',
    title: 'Items & Sealing',
    eyebrow: 'Carry, seal, equip',
    body: [
      'Drops found during a run are carried and unsealed. They become permanent stash items only when extracted at or below their Seal Depth requirement.',
      'There is no extraction before floor 3. Loot found before floor 3 can still be sealed if you survive to a floor 3+ ferry-step.',
      'If you extract too shallow for an item rarity, below-depth items are left behind. If you die, the carried haul is lost.',
    ],
    table: {
      columns: ['Rarity', 'Seal Depth', 'Banking rule'],
      rows: [
        ['Common', 'Floor 3', 'Banks when extracted at floor 3+'],
        ['Uncommon', 'Floor 4', 'Banks when extracted at floor 4+'],
        ['Rare', 'Floor 6', 'Banks when extracted at floor 6+'],
        ['Epic', 'Floor 8', 'Banks when extracted at floor 8+'],
        ['Relic', 'Floor 10', 'Banks when extracted at floor 10+'],
      ],
    },
    bullets: [
      'Any gear you own can be equipped right away, whatever its rarity — dungeon drops and marketplace purchases alike. There is no depth or rarity unlock for equipping.',
      'Seal Depth only controls what banks on extraction: an item extracted above its Seal Depth is left behind.',
      'Normal sealed dungeon drops stay tradeable and sellable by default after extraction.',
      'Equipping unbound dungeon gear from Inventory binds it to your account after a confirmation. Bound items cannot be listed or traded on the Marketplace, but can still be salvaged to the Merchant for fixed Gold.',
      'Equipping a found item during a run does not bind it. If you extract it at the required Seal Depth, it banks as normal unbound gear.',
      'Account-bound, listed, or equipped items have trade/use restrictions depending on the surface.',
      'Items have survival gameplay stats only, such as attack, defense, max HP, max Lantern, Lantern efficiency, burden mitigation, and supply restore/heal bonuses.',
      'No item increases Gold output, loot odds, token payouts, Merchant discounts, fees, Passes, or spinner odds.',
    ],
  },
  {
    id: 'inventory',
    title: 'Inventory',
    eyebrow: 'Stash, loadout, collection',
    body: [
      'Inventory shows your five equipment slots — weapon, armor, lantern, and two trinkets — next to the sealed stash. Equipped items are highlighted in green.',
      'Your cosmetic collection also lives here, under the stash. The Cosmetics page is where you claim and buy skins; the Inventory collection is where you see and equip what you own.',
    ],
    bullets: [
      'Equipping unbound dungeon gear asks for confirmation first, because it binds the item to your account.',
      'Bound items cannot be listed or traded, but can still be salvaged to the Merchant.',
      'Listed items and reserved cosmetic copies are locked until the listing sells or is cancelled.',
      'Cosmetics can be equipped and unequipped freely from the collection; unequipping returns the default look.',
    ],
  },
  {
    id: 'gold',
    title: 'Gold',
    eyebrow: 'Game currency',
    body: [
      'Gold is server-side game currency, not an on-chain token. It is created through successful Reward extraction (the Spinner no longer creates Gold).',
      'Gold is spent on game sinks and player markets: tolls, Merchant supplies, repairs, cosmetics, item marketplace purchases, and other tuned costs.',
    ],
    bullets: [
      'The backend records Gold in the ledger; the client never creates Gold.',
      'Item marketplace trades move Gold between players.',
      'Gold Exchange orders move escrowed Gold to a buyer only after the matching token payment is verified.',
    ],
  },
  {
    id: 'marketplace',
    title: 'Marketplace',
    eyebrow: 'Gear and cosmetics, Gold or $OBOL',
    body: [
      'Gear and cosmetics can be listed for Gold or for $OBOL — the seller picks the listing currency.',
      'Gold listings work exactly as before: the buyer pays banked Gold, the fee is 0%, and the seller receives the full listed amount. No on-chain payment is involved.',
      '$OBOL listings are player-to-player token sales: the buyer pays on-chain from their wallet, the seller receives 95% and the treasury receives 5% (the same split as the Gold Exchange). The server verifies the on-chain payment before the item or cosmetic transfers.',
    ],
    bullets: [
      'Only sealed, non-account-bound, unequipped items can be listed; cosmetic listings reserve a spare copy.',
      'A listed item is locked until sold or cancelled.',
      'Gold buys are backend-settled: Gold moves buyer to seller and ownership transfers in one transaction.',
      'Buying an $OBOL listing reserves it for you for 10 minutes while you pay with your wallet; an unpaid reservation expires and the listing returns to the market.',
      'No server custody: $OBOL goes straight from the buyer to the seller and treasury on-chain — the server only verifies it.',
      '$OBOL marketplace sales never move in-game Gold.',
      'Anything you buy can be equipped immediately — there is no depth or rarity unlock for equipping.',
    ],
  },
  {
    id: 'gold-exchange',
    title: 'Gold Exchange',
    eyebrow: 'Gold sale for token',
    body: [
      'Gold Exchange is specifically the player-to-player Gold sale for $OBOL (item and cosmetic sales live in the Marketplace). The seller lists Gold, the backend escrow-locks that Gold, and a buyer pays the seller and treasury on-chain.',
      'The split is 95% to the seller and 5% to treasury. The backend verifies mint, amounts, recipients, reference, confirmation, and replay protection before changing game state.',
    ],
    bullets: [
      'No server custody: the server does not hold user tokens or sign token transfers.',
      'A wallet transfer outside the exact verified order does not credit in-game Gold.',
      'Cancelled seller orders release the escrowed Gold back to the seller.',
    ],
  },
  {
    id: 'spinner',
    title: 'Spinner',
    eyebrow: "Ferryman's Wager",
    body: [
      'The Spinner never awards Gold or gear/items. The free spin (12-hour default cooldown, active eligibility required) awards account-bound run supplies only: Oil Flasks, Torch Bundles, and Healing Draughts.',
      'The paid spin costs $5 of $OBOL: the backend quotes the exact $OBOL amount at the live price when you create the order, and that quote stays payable for 2 minutes. After it expires you create a new quote.',
      'For paid spins, the split is 50% true burn and 50% treasury. The backend verifies the on-chain payment against the exact stored quote before the server rolls and grants a reward.',
    ],
    bullets: [
      'Free spin prizes: supplies only — never Gold, never gear.',
      'Paid spin prizes: larger supply bundles plus a weekly spin-exclusive cosmetic.',
      'The weekly cosmetic is tradeable, visual-only, never sold in the shop, and can only be newly won during its weekly window.',
      'The client never rolls the paid result locally, and a paid quote is a pure token sink.',
      'If the live $OBOL price is unavailable, the paid spinner pauses (fail-closed); the free spin keeps working.',
    ],
  },
  {
    id: 'cosmetics',
    title: 'Cosmetics',
    eyebrow: 'Visual identity',
    body: [
      'Cosmetics are visual-only. They do not add stats, change combat, improve rewards, reduce fees, or give a gameplay advantage. No gameplay advantage is attached to a skin.',
      'Skins come from four places: token-gated claim campaigns, the Gold shop (including premium limited-stock skins), the weekly spin-exclusive cosmetic on the paid Spinner, and player-to-player trades on the Marketplace.',
      'Your owned collection lives on the Inventory page. The Cosmetics page handles claims and shop purchases.',
    ],
    bullets: [
      'Claim campaigns are token-gated, capped, one-per-account claims — for example the founder Waystation Pilgrim claim, or the time-windowed Blackhorn Warden claim (holding 1,000 $OBOL + 400 $ANSEM during its 10-day window).',
      'Paid launch skins are bought with Gold from the primary shop while stock remains; premium limited skins carry very small stock caps.',
      'You can own multiple copies of the same cosmetic; listing one on the Marketplace reserves a spare copy, never your equipped look.',
      'Tradeable cosmetics can move through the cosmetic marketplace when unequipped and allowed.',
      'Unequipping a skin returns the player to the default appearance.',
    ],
  },
  {
    id: 'spectate',
    title: 'Spectate',
    eyebrow: 'Public preview',
    body: [
      'Spectate is public and read-only. It plays curated demo runs through the same canvas presentation used by normal runs.',
      'Spectating has no wallet requirement, no rewards, and no state mutation.',
    ],
    bullets: [
      'You can watch before connecting.',
      'Spectate controls step, play, pause, and reset the replay only.',
      'Spectate does not expose move, attack, trade, Gold, spinner, or cashout actions.',
    ],
  },
  {
    id: 'safety-risks',
    title: 'Safety & Risks',
    eyebrow: 'Read before play',
    body: [
      'Gravelight is a game with an on-chain token layer. This is no financial advice.',
      'Token price can move, access thresholds and rules can be tuned, RPC providers can fail, and maintenance or bugs can interrupt play.',
      'Holding $OBOL does not promise rewards, payouts, liquidity, or continued access to any specific rule set.',
    ],
    bullets: [
      'Reward runs still require skill, survival, a Ferry Pass, and successful extraction.',
      'Admins can tune game rules, fees, thresholds, odds, and safety limits.',
      'Do not rely on Spectate, Echo, cosmetics, or token holding as a financial plan.',
    ],
  },
  {
    id: 'faq',
    title: 'FAQ',
    eyebrow: 'Common questions',
    body: [],
    table: {
      columns: ['Question', 'Answer'],
      rows: [
        ['Do I need tokens to play?', 'You need token eligibility for Reward runs. Echo runs, Docs, Home, and Spectate are available without it.'],
        ['Can I play for free?', 'You can practice through Echo runs and watch Spectate without token eligibility. Those paths do not grant rewards.'],
        ['What is Gold?', 'Gold is server-side game currency tracked by the backend ledger. It is not an on-chain token.'],
        ['Can I cash out Gold?', 'You can list Gold for sale to another player. The buyer pays $OBOL on-chain, then the backend verifies before moving escrowed Gold.'],
        ['Are cosmetics pay-to-win?', 'No. Cosmetics are visual-only and provide no stats or gameplay advantage.'],
        ['Why can’t I start a Reward run?', 'Reward starts need active token eligibility and at least 1 Ferry Pass. Check the Dashboard, use Re-check after moving tokens, and confirm Passes remain.'],
        ['Why do I have 0 Ferry Passes?', 'Passes only regenerate (+1 every 4 hours, cap 6) after the account activates through a successful eligibility check, and Reward starts spend them.'],
        ['Where did my Echo loot go?', 'Echo runs are practice: loot and Gold always vanish when the Echo ends. Only extracted Reward runs bank anything.'],
        ['Why can’t I extract on floor 1 or 2?', 'There is no ferry step before floor 3. Loot found on floors 1–2 can still bank if you carry it to a floor 3+ extraction.'],
        ['Can I equip a Rare item I bought?', 'Yes. If you own it and it is otherwise valid, you can equip it — there is no depth or rarity unlock for equipping.'],
        ['Why did my paid Spinner quote expire?', 'Paid-spin quotes hold the live $OBOL price for 2 minutes. After that the order expires unpaid and you create a fresh quote.'],
        ['Why is the paid Spinner unavailable?', 'If the live $OBOL price cannot be fetched, paid spins pause (fail-closed) until pricing returns. Free spins keep working.'],
        ['Why does a listing reserve my cosmetic copy?', 'A listed cosmetic must stay deliverable, so one owned copy is locked to the listing until it sells or you cancel.'],
        ['Where is my cosmetic collection?', 'On the Inventory page, under the stash. The Cosmetics page is for claims and shop purchases.'],
        ['What does account-bound mean?', 'The item is tied to your account: it cannot be listed or traded to players. Bound gear can still be equipped, repaired, and salvaged.'],
        ['Can I sell account-bound gear to the Merchant?', 'Yes — salvage accepts bound gear for a small fixed Gold return based on rarity.'],
        ['What does burden do?', 'Carried Gold and items add burden, which makes the dark more dangerous the heavier you travel. Gear with burden mitigation softens it.'],
        ['What happens if I abandon a run?', 'Abandoning counts like dying for the haul: carried Gold and unsealed items are lost. Banked stash and account state stay safe.'],
        ['What happens if I die?', 'The carried haul is lost. Sealed stash items remain on the account.'],
        ['Are Oil, Torch, and Potion infinite?', 'No. They are finite charges inside a run. Reward starts with Oil 2 / Torch 2 / Potion 2 for free; selected extras come from account stock. Echo uses practice supplies.'],
        ['Can I buy repairs?', 'Yes. The Merchant repairs damaged owned gear for Gold using a rarity-scaled cost.'],
        ['What happens if RPC fails?', 'The attempted verification is unverified. No Pass is spent and the account is not frozen by an RPC failure.'],
        ['Is Spectate interactive?', 'Only replay controls are interactive. Spectate cannot mutate game state or grant rewards.'],
        ['Is this an NFT game?', 'No NFT minting is part of v1. Items and cosmetics are game account state; $OBOL is the fungible token used for access and payment flows.'],
      ],
    },
  },
];

function sectionNav(): string {
  return DOC_SECTIONS
    .map((section) => `<a href="#${esc(section.id)}">${esc(section.title)}</a>`)
    .join('');
}

function renderTable(table: DocsSection['table']): string {
  if (!table) return '';
  const head = table.columns.map((column) => `<th>${esc(column)}</th>`).join('');
  const rows = table.rows
    .map((row) => `<tr>${row.map((cell) => `<td>${esc(cell)}</td>`).join('')}</tr>`)
    .join('');
  return `<div class="docs-table-wrap"><table class="docs-table"><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table></div>`;
}

function renderSection(section: DocsSection): string {
  const body = section.body.map((line) => `<p>${esc(line)}</p>`).join('');
  const bullets = section.bullets?.length
    ? `<ul>${section.bullets.map((bullet) => `<li>${esc(bullet)}</li>`).join('')}</ul>`
    : '';
  return `
    <section class="docs-section" id="${esc(section.id)}" data-section-title="${esc(section.title)}">
      <p class="docs-section__eyebrow">${esc(section.eyebrow ?? 'Guide')}</p>
      <h2>${esc(section.title)}</h2>
      ${body}
      ${renderTable(section.table)}
      ${bullets}
    </section>`;
}

export function renderDocs(state: AppState): string {
  const playCta = state.session
    ? btn('Enter the Waystation', 'goto-waystation', {}, { variant: 'primary' })
    : btn('Connect Wallet', 'goto-dashboard', {}, { variant: 'primary' });
  return `
    <section class="docs-shell">
      <header class="docs-hero">
        <div>
          <p class="docs-section__eyebrow">Reference manual</p>
          <h1>Gravelight Docs</h1>
          <p>
            The full reference for the Descent, the Waystation, Gold, $OBOL access,
            markets, spinner, cosmetics, Spectate, and the risks that come with an on-chain game layer.
            New here? Start with the short How To Play guide.
          </p>
          <div class="btn-row">
            ${playCta}
            ${btn('How To Play', 'goto-how-to-play', {}, { variant: 'ghost' })}
            ${btn('Spectate a Run', 'goto-spectate', {}, { variant: 'ghost' })}
            ${btn('Home', 'goto-landing', {}, { variant: 'ghost' })}
          </div>
        </div>
        <div class="docs-hero__plate" aria-hidden="true">
          <img src="${versionedAssetUrl('/assets/sprites/ui/ui_lantern_01.png')}" alt="" loading="lazy">
          <img src="${versionedAssetUrl('/assets/sprites/ui/ui_gold_01.png')}" alt="" loading="lazy">
          <img src="${versionedAssetUrl('/assets/sprites/ui/ui_ferry_pass_01.png')}" alt="" loading="lazy">
        </div>
      </header>

      <div class="docs-layout">
        <nav class="docs-nav" aria-label="Docs sections">
          ${sectionNav()}
        </nav>
        <div class="docs-content">
          ${DOC_SECTIONS.map(renderSection).join('')}
        </div>
      </div>
    </section>`;
}
